/**
 * Reading and writing an item's document by path ("playback.video.videoId", "recipe.steps.2.text").
 * A value that is cleared is removed, and so is an object it leaves empty, so what is saved is
 * always what an editor meant (an empty string or {} would fail the feed schema). Pure.
 */

export type Doc = Record<string, unknown>;

const isEmpty = (v: unknown): boolean =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0) || (typeof v === "object" && !Array.isArray(v) && Object.keys(v as Doc).length === 0);

const key = (part: string): string | number => (/^\d+$/.test(part) ? Number(part) : part);

export function getPath(doc: unknown, path: string): unknown {
  let at: unknown = doc;
  for (const part of path.split(".")) {
    if (at === null || typeof at !== "object") return undefined;
    at = (at as Record<string | number, unknown>)[key(part)];
  }
  return at;
}

/**
 * A copy of `doc` with `path` set to `value`. Empty values remove the key, and objects left empty
 * on the way up are removed too; arrays keep their length (an empty array is removed only at the
 * leaf, `keepEmpty` keeps it there for lists the schema requires).
 */
export function setPath(doc: Doc, path: string, value: unknown, keepEmpty = false): Doc {
  const parts = path.split(".");
  const write = (node: unknown, i: number): unknown => {
    const k = key(parts[i]!);
    if (Array.isArray(node) || typeof k === "number") {
      const list = Array.isArray(node) ? [...node] : [];
      const index = Number(k);
      list[index] = i === parts.length - 1 ? value : write(list[index], i + 1);
      return list;
    }
    const obj: Doc = node && typeof node === "object" ? { ...(node as Doc) } : {};
    const next = i === parts.length - 1 ? value : write(obj[k], i + 1);
    if (isEmpty(next) && !(keepEmpty && i === parts.length - 1 && Array.isArray(next))) delete obj[k];
    else obj[k] = next;
    return obj;
  };
  return write(doc, 0) as Doc;
}

/** The DOM id a field at this path is drawn with, so an error summary and Review & publish can open it. */
export function fieldDomId(path: string): string {
  return `f-${path.replace(/[^A-Za-z0-9_-]+/g, "-")}`;
}

/** The path and every path above it, most specific first: "a.b.c" → a.b.c, a.b, a. */
export function pathAndParents(path: string): string[] {
  const parts = path.split(".");
  return parts.map((_, i) => parts.slice(0, parts.length - i).join("."));
}
